/**
 * Error logging types and constants for DiceCapture web-app
 * @module errorLog
 */

/**
 * Severity levels for error log entries
 */
export type ErrorSeverity = 'error' | 'warning' | 'info';

/**
 * Error source categories matching existing SERVICE_TAG patterns
 */
export type ErrorSource =
  | 'CAMERA'
  | 'WEBSOCKET'
  | 'RECOGNITION'
  | 'DICE_REGISTRY'
  | 'SESSION'
  | 'SOULBONDS'
  | 'IMAGE'
  | 'CONFIG'
  | 'BRIDGE'
  | 'FTP'
  | 'MOTION'
  | 'VIDEO'
  | 'STORAGE'
  | 'DISCORD'
  | 'UNKNOWN';

/**
 * Error log entry persisted to localStorage
 */
export interface ErrorLogEntry {
  /** Unique identifier */
  id: string;
  /** ISO timestamp */
  timestamp: string;
  /** Error severity level */
  severity: ErrorSeverity;
  /** Source service/store (maps to SERVICE_TAG) */
  source: ErrorSource;
  /** User-friendly message */
  userMessage: string;
  /** Technical details for debugging */
  technicalDetails?: string;
  /** Original error message */
  originalError?: string;
  /** Context data (e.g., operation name, identifiers) */
  context?: Record<string, unknown>;
  /** Whether user dismissed the toast */
  acknowledged: boolean;
}

/**
 * Options for reporting an error
 */
export interface ReportErrorOptions {
  /** Source service tag (without brackets) */
  source: ErrorSource;
  /** User-friendly message to display */
  userMessage: string;
  /** Technical details for developers */
  technicalDetails?: string;
  /** Original error object/message */
  originalError?: Error | string;
  /** Additional context */
  context?: Record<string, unknown>;
  /** Severity level (default: 'error') */
  severity?: ErrorSeverity;
  /** Show toast notification (default: true) */
  showToast?: boolean;
  /** Toast duration in ms (default: 5000 for error/warning, 3000 for info) */
  toastDuration?: number;
}

/**
 * Error message prefixes by severity
 */
export const ERROR_PREFIXES: Record<ErrorSeverity, string> = {
  error: 'Something went wrong',
  warning: 'Heads up',
  info: 'Note'
};

/**
 * Source-specific friendly names for user-facing display
 */
export const FRIENDLY_SOURCE_NAMES: Record<ErrorSource, string> = {
  CAMERA: 'Camera',
  WEBSOCKET: 'Connection',
  RECOGNITION: 'Recognition',
  DICE_REGISTRY: 'Dice Registry',
  SESSION: 'Session',
  SOULBONDS: 'Profiles',
  IMAGE: 'Image Capture',
  CONFIG: 'Settings',
  BRIDGE: 'Bridge Server',
  FTP: 'Remote Archive',
  MOTION: 'Motion Sensing',
  VIDEO: 'Video Capture',
  STORAGE: 'Storage',
  DISCORD: 'Discord',
  UNKNOWN: 'Unknown'
};
